import React, { useState, useRef, useEffect } from 'react';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { bookingTokens } from './bookingDetailsTheme';

const DOCUMENT_TYPES = [
  { value: 'id_proof', label: 'ID Proof', icon: '🪪', hint: 'Aadhaar, Passport, Voter ID or Driving License' },
  { value: 'address_proof', label: 'Address Proof', icon: '🏠', hint: 'Utility bill, Aadhaar or rental agreement' },
  { value: 'bank_statement', label: 'Bank Statement', icon: '🏦', hint: 'Last 6 months bank statement' },
  { value: 'income_proof', label: 'Income Proof', icon: '💰', hint: 'Salary slips or ITR for last 2 years' },
  { value: 'loan_sanction', label: 'Loan Sanction Letter', icon: '📜', hint: 'Bank-issued loan sanction document' },
  { value: 'property_docs', label: 'Property Documents', icon: '🏗️', hint: 'Sale deed, title deed or allotment letter' },
  { value: 'occupancy_cert', label: 'Occupancy Certificate', icon: '🔑', hint: 'Issued by local municipal authority' },
  { value: 'building_plan', label: 'Approved Building Plan', icon: '📐', hint: 'Sanctioned building plan copy' },
  { value: 'completion_cert', label: 'Completion Certificate', icon: '✅', hint: 'Issued after construction completion' },
  { value: 'pan', label: 'PAN Card', icon: '💳', hint: 'Permanent Account Number card' },
  { value: 'passport_photo', label: 'Passport-size Photographs', icon: '📷', hint: 'Recent passport-size colour photo' },
  { value: 'power_of_attorney', label: 'Power of Attorney', icon: '⚖️', hint: 'Notarised power of attorney document' },
  { value: 'gst_cert', label: 'GST Registration Certificate', icon: '📋', hint: 'GST registration for business buyers' },
  { value: 'affidavit', label: 'Affidavit / Declaration', icon: '📑', hint: 'Notarised affidavit or declaration form' },
  { value: 'other', label: 'Other Documents', icon: '📎', hint: 'Any other supporting document' },
];

const formatSize = (b) => (b < 1024 * 1024 ? (b / 1024).toFixed(1) + ' KB' : (b / 1024 / 1024).toFixed(2) + ' MB');
const getFileIcon = (name = '') => {
  const ext = name.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['doc', 'docx'].includes(ext)) return '📝';
  return '📎';
};

function DocTypeDropdown({ value, onChange, error, options }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find((d) => d.value === value);
  const filtered = search ? options.filter((d) => d.label.toLowerCase().includes(search.toLowerCase())) : options;

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const handleSelect = (dt) => {
    onChange(dt);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="bdu-ddt-wrap" ref={ref}>
      <button
        type="button"
        className={`bdu-ddt-trigger ${open ? 'open' : ''} ${error ? 'err' : ''} ${selected ? 'has-val' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        {selected ? (
          <span className="bdu-ddt-trigger-val">
            <span className="bdu-ddt-trigger-icon">{selected.icon || '📄'}</span>
            <span className="bdu-ddt-trigger-label">{selected.label}</span>
          </span>
        ) : (
          <span className="bdu-ddt-placeholder">Select document type…</span>
        )}
        <span className={`bdu-ddt-arrow ${open ? 'up' : ''}`}>▾</span>
      </button>

      {open && (
        <div className="bdu-ddt-dropdown">
          <div className="bdu-ddt-search-wrap">
            <span className="bdu-ddt-search-icon">🔍</span>
            <input
              ref={searchRef}
              className="bdu-ddt-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search document type…"
            />
            {search && (
              <button type="button" className="bdu-ddt-search-clear" onClick={() => setSearch('')}>
                ✕
              </button>
            )}
          </div>
          <ul className="bdu-ddt-list">
            {filtered.length === 0 && <li className="bdu-ddt-empty">No results for "{search}"</li>}
            {filtered.map((dt) => (
              <li
                key={dt.value}
                className={`bdu-ddt-item ${value === dt.value ? 'selected' : ''}`}
                onClick={() => handleSelect(dt)}
                onKeyDown={(e) => e.key === 'Enter' && handleSelect(dt)}
                role="option"
                aria-selected={value === dt.value}
              >
                <span className="bdu-ddt-item-icon">{dt.icon || '📄'}</span>
                <span className="bdu-ddt-item-text">
                  <span className="bdu-ddt-item-label">{dt.label}</span>
                  {dt.hint && <span className="bdu-ddt-item-hint">{dt.hint}</span>}
                </span>
                {value === dt.value && <span className="bdu-ddt-check">✓</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const AddBookingDocument = ({
  open,
  onClose,
  bookingId,
  documentTypeOptions = [],
  onSuccess,
}) => {
  const { addBookingDocument } = useBookingDetails();
  const [docType, setDocType] = useState('');
  const [docName, setDocName] = useState('');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState({});
  const [flash, setFlash] = useState(null);
  const [mounted, setMounted] = useState(false);

  const options =
    documentTypeOptions?.length > 0
      ? documentTypeOptions.map((d) => {
          const rich = DOCUMENT_TYPES.find((t) => t.value === (d.value || d.id));
          return { value: d.value || d.id, label: d.label || d.name || d.value, icon: rich?.icon || '📄', hint: rich?.hint };
        })
      : DOCUMENT_TYPES;

  useEffect(() => {
    if (open) {
      setDocType('');
      setDocName('');
      setFile(null);
      setErrors({});
      setProgress(0);
      setLoading(false);
      setTimeout(() => setMounted(true), 10);
    } else {
      setMounted(false);
    }
  }, [open]);

  if (!open) return null;

  const resetForm = () => {
    setDocType('');
    setDocName('');
    setFile(null);
    setErrors({});
    setProgress(0);
    setLoading(false);
  };
  const showFlash = (type, msg) => {
    setFlash({ type, msg });
    setTimeout(() => setFlash(null), 3000);
  };

  const validateFile = (f) => {
    if (f.size > 10 * 1024 * 1024) return 'File must be under 10MB';
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    if (!['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'].includes(ext)) return 'Unsupported file type';
    return null;
  };

  const handleFileSet = (f) => {
    const err = validateFile(f);
    if (err) {
      setErrors((p) => ({ ...p, file: err }));
      return;
    }
    setFile(f);
    setErrors((p) => ({ ...p, file: null }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type !== 'dragleave');
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileSet(e.dataTransfer.files[0]);
  };

  const handleTypeSelect = (dt) => {
    setDocType(dt.value);
    setErrors((p) => ({ ...p, docType: null }));
    if (!docName) setDocName(dt.label);
  };

  const validate = () => {
    const e = {};
    if (!docType) e.docType = 'Please select a document type';
    if (!docName.trim()) e.docName = 'Please enter a document name';
    if (!file) e.file = 'Please select a file to upload';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpload = async () => {
    if (!validate()) return;
    setLoading(true);
    setProgress(0);
    const iv = setInterval(() => setProgress((p) => Math.min(p + Math.random() * 18, 90)), 160);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      formData.append('title', docName.trim());

      await addBookingDocument(bookingId, formData);

      setProgress(100);
      clearInterval(iv);
      showFlash('success', '✓ Document uploaded successfully');
      if (onSuccess) onSuccess();
      setTimeout(handleClose, 800);
    } catch (err) {
      clearInterval(iv);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.title?.[0] ||
        err?.response?.data?.document_type?.[0] ||
        err?.response?.data?.file?.[0] ||
        err?.message ||
        'Upload failed';
      showFlash('error', msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose();
  };

  const selectedType = options.find((d) => d.value === docType);
  const g = bookingTokens.green;
  const gMd = bookingTokens.greenMd;
  const gLt = bookingTokens.greenLt;
  const border = bookingTokens.border;
  const borderL = bookingTokens.borderL;

  return (
    <>
      <style>{`
        .bdu-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.45);
          display: flex; align-items: center; justify-content: center;
          z-index: 9999; padding: 16px;
          opacity: 0; transition: opacity 0.25s ease;
        }
        .bdu-overlay.in { opacity: 1; }
        .bdu-panel {
          background: ${bookingTokens.bg};
          border-radius: 20px;
          width: 100%; max-width: 500px; max-height: 94vh;
          display: flex; flex-direction: column;
          box-shadow: 0 24px 60px rgba(0,0,0,0.28);
          transform: translateY(20px) scale(0.98); opacity: 0;
          transition: transform 0.3s cubic-bezier(0.34,1.4,0.64,1), opacity 0.25s ease;
          overflow: hidden; font-family: 'Inter', system-ui, sans-serif;
        }
        .bdu-panel.in { transform: translateY(0) scale(1); opacity: 1; }

        .bdu-header {
          background: linear-gradient(135deg, ${g} 0%, ${gMd} 100%);
          padding: 20px 22px 18px;
          display: flex; align-items: center; gap: 14px; flex-shrink: 0;
        }
        .bdu-header-icon {
          width: 52px; height: 52px; border-radius: 14px;
          background: rgba(255,255,255,0.15);
          display: flex; align-items: center; justify-content: center;
          font-size: 26px; flex-shrink: 0; box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
        .bdu-header-text { flex: 1; }
        .bdu-header-title { font-size: 20px; font-weight: 700; color: #fff; margin: 0 0 2px; }
        .bdu-header-sub { font-size: 13px; color: rgba(255,255,255,0.72); margin: 0; }
        .bdu-close-btn {
          width: 34px; height: 34px; border-radius: 10px;
          background: rgba(255,255,255,0.18); border: none; color: #fff;
          font-size: 15px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.2s; flex-shrink: 0;
        }
        .bdu-close-btn:hover { background: rgba(255,255,255,0.28); }

        .bdu-body { flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 10px; }
        .bdu-body::-webkit-scrollbar { width: 4px; }
        .bdu-body::-webkit-scrollbar-thumb { background: ${border}; border-radius: 4px; }

        .bdu-card { background: ${bookingTokens.white}; border-radius: 16px; padding: 16px 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); border: 1px solid ${borderL}; }
        .bdu-card-header { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
        .bdu-card-icon { font-size: 20px; line-height: 1; }
        .bdu-card-title { font-size: 15px; font-weight: 700; color: ${bookingTokens.ink}; }

        .bdu-label { font-size: 12px; font-weight: 600; color: ${g}; margin-bottom: 6px; display: flex; align-items: center; gap: 3px; }
        .bdu-required { color: ${bookingTokens.red}; }
        .bdu-field { margin-bottom: 12px; }
        .bdu-field:last-child { margin-bottom: 0; }

        .bdu-ddt-wrap { position: relative; }
        .bdu-ddt-trigger {
          width: 100%; padding: 10px 14px;
          border: 1.5px solid ${border}; border-radius: 10px;
          background: ${bookingTokens.white}; font-family: inherit;
          font-size: 14px; color: ${bookingTokens.ink}; cursor: pointer;
          display: flex; align-items: center; justify-content: space-between;
          gap: 8px; text-align: left; outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .bdu-ddt-trigger:hover, .bdu-ddt-trigger.open { border-color: ${g}; }
        .bdu-ddt-trigger.open { box-shadow: 0 0 0 3px ${bookingTokens.greenSf}; border-radius: 10px 10px 0 0; border-bottom-color: transparent; }
        .bdu-ddt-trigger.err { border-color: ${bookingTokens.redBdr}; }
        .bdu-ddt-trigger-val { display: flex; align-items: center; gap: 10px; flex: 1; }
        .bdu-ddt-trigger-icon { font-size: 20px; line-height: 1; flex-shrink: 0; }
        .bdu-ddt-trigger-label { font-size: 14px; font-weight: 500; color: ${bookingTokens.ink}; }
        .bdu-ddt-placeholder { color: ${bookingTokens.muted}; font-size: 14px; }
        .bdu-ddt-arrow { font-size: 13px; color: ${bookingTokens.muted}; transition: transform 0.2s; flex-shrink: 0; }
        .bdu-ddt-arrow.up { transform: rotate(180deg); }

        .bdu-ddt-dropdown {
          position: absolute; top: 100%; left: 0; right: 0;
          background: ${bookingTokens.white};
          border: 1.5px solid ${g}; border-top: none;
          border-radius: 0 0 12px 12px;
          box-shadow: 0 12px 32px rgba(0,0,0,0.14);
          z-index: 100; overflow: hidden;
        }
        .bdu-ddt-search-wrap {
          display: flex; align-items: center; gap: 8px;
          padding: 10px 14px; border-bottom: 1px solid ${borderL};
          background: ${bookingTokens.bg};
        }
        .bdu-ddt-search-icon { font-size: 14px; flex-shrink: 0; }
        .bdu-ddt-search { flex: 1; border: none; outline: none; background: transparent; font-family: inherit; font-size: 13px; color: ${bookingTokens.ink}; }
        .bdu-ddt-search::placeholder { color: ${bookingTokens.muted}; }
        .bdu-ddt-search-clear { background: none; border: none; color: ${bookingTokens.muted}; cursor: pointer; font-size: 12px; padding: 0; display: flex; align-items: center; }
        .bdu-ddt-search-clear:hover { color: ${bookingTokens.ink2}; }

        .bdu-ddt-list { list-style: none; margin: 0; padding: 4px 0; max-height: 240px; overflow-y: auto; }
        .bdu-ddt-list::-webkit-scrollbar { width: 4px; }
        .bdu-ddt-list::-webkit-scrollbar-thumb { background: ${border}; border-radius: 4px; }

        .bdu-ddt-item {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 14px; cursor: pointer;
          transition: background 0.13s;
          border-bottom: 1px solid ${borderL};
        }
        .bdu-ddt-item:last-child { border-bottom: none; }
        .bdu-ddt-item:hover { background: ${gLt}; }
        .bdu-ddt-item.selected { background: ${gLt}; }
        .bdu-ddt-item-icon { font-size: 20px; line-height: 1; flex-shrink: 0; width: 28px; text-align: center; }
        .bdu-ddt-item-text { flex: 1; min-width: 0; }
        .bdu-ddt-item-label { font-size: 13px; font-weight: 600; color: ${bookingTokens.ink}; display: block; }
        .bdu-ddt-item.selected .bdu-ddt-item-label { color: ${g}; }
        .bdu-ddt-item-hint { font-size: 11px; color: ${bookingTokens.muted}; display: block; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .bdu-ddt-check { font-size: 13px; color: ${g}; font-weight: 700; flex-shrink: 0; }
        .bdu-ddt-empty { padding: 16px 14px; font-size: 13px; color: ${bookingTokens.muted}; font-style: italic; }

        .bdu-type-hint {
          display: flex; align-items: center; gap: 8px;
          margin-top: 8px; padding: 9px 12px;
          background: ${gLt}; border: 1px solid ${border}; border-radius: 8px;
        }
        .bdu-hint-text { font-size: 12px; color: ${g}; font-weight: 500; }

        .bdu-input {
          width: 100%; padding: 10px 14px;
          border: 1.5px solid ${border}; border-radius: 10px;
          background: ${bookingTokens.white}; font-family: inherit;
          font-size: 14px; color: ${bookingTokens.ink}; outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .bdu-input:focus { border-color: ${g}; box-shadow: 0 0 0 3px ${bookingTokens.greenSf}; }
        .bdu-input.err { border-color: ${bookingTokens.redBdr}; }

        .bdu-dropzone {
          border: 2px dashed ${border}; border-radius: 12px;
          padding: 28px 20px; text-align: center; cursor: pointer;
          background: ${borderL}; transition: all 0.2s;
        }
        .bdu-dropzone:hover, .bdu-dropzone.drag { border-color: ${g}; background: ${gLt}; }
        .bdu-dropzone.err { border-color: ${bookingTokens.redBdr}; background: ${bookingTokens.redLt}; }
        .bdu-dz-icon {
          width: 52px; height: 52px; border-radius: 14px;
          background: ${gLt}; border: 2px solid ${border};
          display: flex; align-items: center; justify-content: center;
          font-size: 24px; margin: 0 auto 10px; transition: all 0.2s;
        }
        .bdu-dropzone:hover .bdu-dz-icon, .bdu-dropzone.drag .bdu-dz-icon { background: ${gLt}; border-color: ${g}; }
        .bdu-dz-title { font-size: 14px; font-weight: 600; color: ${bookingTokens.ink}; margin: 0 0 4px; }
        .bdu-dz-sub { font-size: 12px; color: ${bookingTokens.muted}; margin: 0 0 12px; }
        .bdu-browse { display: inline-flex; align-items: center; gap: 5px; padding: 7px 16px; border-radius: 8px; background: linear-gradient(135deg, ${g}, ${gMd}); color: #fff; border: none; font-family: inherit; font-size: 12px; font-weight: 600; cursor: pointer; transition: background 0.2s; }
        .bdu-browse:hover { background: linear-gradient(135deg, ${gMd}, ${g}); }
        .bdu-dz-formats { display: flex; flex-wrap: wrap; gap: 5px; justify-content: center; margin-top: 12px; }
        .bdu-fmt-chip { font-size: 9px; font-weight: 700; color: ${g}; background: ${gLt}; border: 1px solid ${border}; padding: 2px 7px; border-radius: 20px; letter-spacing: 0.05em; }

        .bdu-file-card {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 14px; border-radius: 10px;
          border: 1.5px solid ${border}; background: ${gLt};
        }
        .bdu-file-icon-box { width: 40px; height: 40px; border-radius: 10px; background: ${bookingTokens.white}; border: 1px solid ${border}; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
        .bdu-file-info { flex: 1; min-width: 0; }
        .bdu-file-name { font-size: 13px; font-weight: 600; color: ${bookingTokens.ink}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .bdu-file-meta { font-size: 11px; color: ${bookingTokens.muted}; margin-top: 1px; }
        .bdu-file-del { width: 28px; height: 28px; border-radius: 7px; background: ${bookingTokens.redLt}; border: 1px solid ${bookingTokens.redBdr}; color: ${bookingTokens.red}; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 12px; flex-shrink: 0; transition: all 0.15s; }
        .bdu-file-del:hover { background: #fecaca; }

        .bdu-progress { margin-top: 10px; }
        .bdu-progress-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
        .bdu-progress-label { font-size: 11px; color: ${bookingTokens.muted}; font-weight: 500; }
        .bdu-progress-pct { font-size: 11px; color: ${g}; font-weight: 700; }
        .bdu-track { height: 5px; background: ${border}; border-radius: 99px; overflow: hidden; }
        .bdu-fill { height: 100%; background: linear-gradient(90deg, ${g}, ${gMd}); border-radius: 99px; transition: width 0.2s ease; }

        .bdu-field-err { font-size: 11px; color: ${bookingTokens.red}; font-weight: 500; margin-top: 5px; display: flex; align-items: center; gap: 4px; }

        .bdu-flash { position: fixed; top: 20px; left: 50%; transform: translateX(-50%); padding: 10px 20px; border-radius: 10px; font-family: inherit; font-size: 13px; font-weight: 600; white-space: nowrap; z-index: 10001; animation: bduFlash 0.3s ease; }
        .bdu-flash.success { background: ${g}; color: #fff; box-shadow: 0 4px 16px rgba(10,107,62,0.4); }
        .bdu-flash.error { background: ${bookingTokens.red}; color: #fff; box-shadow: 0 4px 16px rgba(220,38,38,0.35); }
        @keyframes bduFlash { from { opacity: 0; transform: translateX(-50%) translateY(-8px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }

        .bdu-footer { padding: 14px 16px; background: ${bookingTokens.white}; border-top: 1px solid ${border}; display: flex; justify-content: flex-end; gap: 10px; flex-shrink: 0; }
        .bdu-cancel-btn { padding: 11px 20px; border-radius: 12px; background: ${bookingTokens.redLt}; border: 1.5px solid ${bookingTokens.redBdr}; color: ${bookingTokens.red}; font-family: inherit; font-size: 14px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s; }
        .bdu-cancel-btn:hover { background: #fecaca; }
        .bdu-cancel-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .bdu-save-btn { padding: 11px 22px; border-radius: 12px; background: linear-gradient(135deg, ${g}, ${gMd}); border: none; color: #fff; font-family: inherit; font-size: 14px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 7px; transition: all 0.2s; box-shadow: 0 2px 8px rgba(10,107,62,0.35); }
        .bdu-save-btn:hover:not(:disabled) { background: linear-gradient(135deg, ${gMd}, ${g}); transform: translateY(-1px); box-shadow: 0 4px 14px rgba(10,107,62,0.4); }
        .bdu-save-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
        .bdu-spinner { width: 15px; height: 15px; border: 2px solid rgba(255,255,255,0.35); border-top-color: #fff; border-radius: 50%; animation: bduSpin 0.7s linear infinite; }
        @keyframes bduSpin { to { transform: rotate(360deg); } }
      `}</style>

      {flash && <div className={`bdu-flash ${flash.type}`} role="alert">{flash.msg}</div>}

      <div
        className={`bdu-overlay ${mounted ? 'in' : ''}`}
        onClick={(e) => e.target === e.currentTarget && handleClose()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bdu-title"
      >
        <div className={`bdu-panel ${mounted ? 'in' : ''}`}>
          <div className="bdu-header">
            <div className="bdu-header-icon">📁</div>
            <div className="bdu-header-text">
              <h2 id="bdu-title" className="bdu-header-title">Upload Document</h2>
              <p className="bdu-header-sub">{bookingId ? `Booking document` : 'Attach KYC or financial documents'}</p>
            </div>
            <button type="button" className="bdu-close-btn" onClick={handleClose} disabled={loading} aria-label="Close">
              ✕
            </button>
          </div>

          <div className="bdu-body">
            <div className="bdu-card">
              <div className="bdu-card-header">
                <span className="bdu-card-icon">🗂️</span>
                <span className="bdu-card-title">Document Details</span>
              </div>

              <div className="bdu-field">
                <div className="bdu-label">
                  Document Type <span className="bdu-required">*</span>
                </div>
                <DocTypeDropdown
                  value={docType}
                  onChange={handleTypeSelect}
                  error={!!errors.docType}
                  options={options}
                />
                {errors.docType && <div className="bdu-field-err">⚠ {errors.docType}</div>}
                {selectedType && (
                  <div className="bdu-type-hint">
                    <span>{selectedType.icon}</span>
                    <span className="bdu-hint-text">{selectedType.hint}</span>
                  </div>
                )}
              </div>

              <div className="bdu-field">
                <label className="bdu-label" htmlFor="bdu-name">
                  Document Name <span className="bdu-required">*</span>
                </label>
                <input
                  id="bdu-name"
                  className={`bdu-input ${errors.docName ? 'err' : ''}`}
                  type="text"
                  value={docName}
                  onChange={(e) => {
                    setDocName(e.target.value);
                    setErrors((p) => ({ ...p, docName: null }));
                  }}
                  placeholder="e.g. Rahul Kumar – Aadhaar Front & Back"
                  disabled={loading}
                />
                {errors.docName && <div className="bdu-field-err">⚠ {errors.docName}</div>}
              </div>
            </div>

            <div className="bdu-card">
              <div className="bdu-card-header">
                <span className="bdu-card-icon">☁️</span>
                <span className="bdu-card-title">Attach File</span>
              </div>

              <input
                id="bdu-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                style={{ display: 'none' }}
                onChange={(e) => e.target.files?.[0] && handleFileSet(e.target.files[0])}
                disabled={loading}
              />

              {file ? (
                <>
                  <div className="bdu-file-card">
                    <div className="bdu-file-icon-box">{getFileIcon(file.name)}</div>
                    <div className="bdu-file-info">
                      <div className="bdu-file-name">{file.name}</div>
                      <div className="bdu-file-meta">{formatSize(file.size)}</div>
                    </div>
                    {!loading && (
                      <button
                        type="button"
                        className="bdu-file-del"
                        onClick={() => {
                          setFile(null);
                          setErrors((p) => ({ ...p, file: null }));
                        }}
                        aria-label="Remove file"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {loading && (
                    <div className="bdu-progress">
                      <div className="bdu-progress-row">
                        <span className="bdu-progress-label">Uploading…</span>
                        <span className="bdu-progress-pct">{Math.round(progress)}%</span>
                      </div>
                      <div className="bdu-track">
                        <div className="bdu-fill" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div
                  className={`bdu-dropzone ${dragActive ? 'drag' : ''} ${errors.file ? 'err' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => !loading && document.getElementById('bdu-file').click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => !loading && (e.key === 'Enter' || e.key === ' ') && document.getElementById('bdu-file').click()}
                >
                  <div className="bdu-dz-icon">📤</div>
                  <p className="bdu-dz-title">Drag & drop file here</p>
                  <p className="bdu-dz-sub">or click to browse from your device</p>
                  <button
                    type="button"
                    className="bdu-browse"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById('bdu-file').click();
                    }}
                    disabled={loading}
                  >
                    📂 Browse File
                  </button>
                  <div className="bdu-dz-formats">
                    {['PDF', 'JPG', 'PNG', 'DOC', 'DOCX'].map((f) => (
                      <span key={f} className="bdu-fmt-chip">
                        {f}
                      </span>
                    ))}
                    <span className="bdu-fmt-chip">Max 10MB</span>
                  </div>
                </div>
              )}
              {errors.file && (
                <div className="bdu-field-err" style={{ marginTop: 6 }}>
                  ⚠ {errors.file}
                </div>
              )}
            </div>
          </div>

          <div className="bdu-footer">
            <button type="button" className="bdu-cancel-btn" onClick={handleClose} disabled={loading}>
              ✕ Cancel
            </button>
            <button type="button" className="bdu-save-btn" onClick={handleUpload} disabled={loading}>
              {loading ? (
                <>
                  <div className="bdu-spinner" /> Uploading…
                </>
              ) : (
                <>☁️ Upload Document</>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddBookingDocument;
